import {useCallback, useState} from 'react'
import {useTranslation} from "react-i18next";
import {CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe} from "@stripe/react-stripe-js";
import cardMasterCard from "assets/images/stripe/card-mastercard.svg";
import cardVisa from "assets/images/stripe/card-visa.svg";
import Icon from "components/atom/Icon.tsx";
import Button from "components/atom/button/Button.tsx";
import LoadingContainer from "components/container/LoadingContainer";
import {tryAsyncFunc} from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import FormLabel from "components/form/FormLabel.tsx";
import FormInput from "components/form/FormInput.tsx";

const style = {
    base: {
        iconColor: '#000',
        color: '#000',
        lineHeight: '20px',
        fontWeight: 400,
        fontFamily: 'Poppins',
        fontSize: '14px',
        fontSmoothing: 'antialiased',
        padding: '9px',

        ':-webkit-autofill': {
            color: '#fce883',
        },

        '::placeholder': {
            color: '#4b5563',
        },
    },
    invalid: {
        iconColor: '#f87171',
        color: '#f87171',
    },
};


type PropsType = {
    onFinish: () => void
    onCancel: () => void
}

function CreditCardForm(props: PropsType) {
    const {t} = useTranslation();
    const stripe = useStripe();
    const elements = useElements();


    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [cardOwnerName, setCardOwnerName] = useState<string>('')
    const [zipcode, setZipcode] = useState<string>('')
    const [errors, setErrors] = useState<any>({})


    const handleSubmit = useCallback(async (event: any) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true)


        setErrors({})
        const cardNumberElement = elements.getElement(CardNumberElement)
        const cardExpiryElement = elements.getElement(CardExpiryElement)
        const cardCvcElement = elements.getElement(CardCvcElement)

        if (!cardNumberElement) return
        if (!cardExpiryElement) return
        if (!cardCvcElement) return

        const errors: any = {}

        //@ts-expect-error stripe issue
        if (cardNumberElement._component.parentNode.classList.contains('StripeElement--empty')) {
            errors.cardNumber = t('forms.errors.cardNumber')
        }
        //@ts-expect-error stripe issue
        if (cardExpiryElement._component.parentNode.classList.contains('StripeElement--empty')) {
            errors.cardExpiry = t('forms.errors.cardExpiry')
        }
        //@ts-expect-error stripe issue
        if (cardCvcElement._component.parentNode.classList.contains('StripeElement--empty')) {
            errors.cardCvc = t('forms.errors.cardCvc')
        }

        if (!zipcode) {
            errors.zipcode = t('forms.errors.zipcode')
        }

        if (!cardOwnerName) {
            errors.cardOwnerName = t('forms.errors.requiredName')
        }

        if (Object.keys(errors).length > 0) {
            setErrors(errors)
            setIsLoading(false)
            return
        }


        const {paymentMethod, error} = await stripe.createPaymentMethod({
            // type:'card',
            element: cardNumberElement,
            params: {
                billing_details: {
                    name: cardOwnerName,
                    address: {
                        postal_code: zipcode,
                        country: 'CA',
                    },
                },
            }
        })

        if (error) {
            setErrors({
                global: error.message
            })
            setIsLoading(false)
            return
        }
        const [_, err] = await tryAsyncFunc(ajax.post(`/connected/me/payment-methods`, {
            paymentMethodId: paymentMethod?.id
        }))

        if (err) {
            const errData = err.response.data || {}
            const errors = errData.errors || {}

            if (errors.global) {
                setErrors({global: errors.global})
                delete errors.global
            }
            setIsLoading(false)
            return
        }

        setIsLoading(false)
        props.onFinish();
    }, [cardOwnerName, elements, props, stripe, t, zipcode])

    return (
        <div className="my-6">
            <h3 className="mb-2 font-semibold">
                {t('stripe.addCard')}
            </h3>
            <LoadingContainer isLoading={isLoading} keepChildren useOpacity>
                <div className="form">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="">
                            <div className="c__input">
                                <FormLabel required={true}>
                                    {t('forms.cardOwnerName')}
                                </FormLabel>
                                <FormInput formikField={false} name="cardOwnerName" value={cardOwnerName}
                                           onInput={(e) => setCardOwnerName(e.target.value)}/>
                                {errors.cardOwnerName && (
                                    <p className="error">{errors.cardOwnerName}</p>
                                )}
                            </div>

                        </div>
                        <div className="">
                            <div className="c__input">
                                <FormLabel required={true}>
                                    {t('forms.zipcode')}
                                </FormLabel>
                                <FormInput placeholder="A1A 1A1" formikField={false} name="zipcode" value={zipcode}
                                           onInput={(e) => setZipcode(e.target.value)}/>
                                {errors.zipcode && (
                                    <p className="error">{errors.zipcode}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="c__input">
                        <FormLabel required={true}>
                            {t('forms.cardNumber')}
                        </FormLabel>
                        <div className="relative">
                            <CardNumberElement options={{style}}/>
                            <div className="absolute right-2 top-2 flex flex-row items-center justify-start gap-3">
                                <img className="h-auto w-9" role="presentation"
                                     src={cardMasterCard}
                                />
                                <img className="h-auto w-9" role="presentation"
                                     src={cardVisa}
                                />
                            </div>
                        </div>
                        {errors.cardNumber && (
                            <p className="error">{errors.cardNumber}</p>
                        )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="">
                            <div className="c__input">
                                <FormLabel required={true}>
                                    {t('forms.cardExpiry')}
                                </FormLabel>
                                <CardExpiryElement options={{style}}/>
                                {errors.cardExpiry && (
                                    <p className="error">{errors.cardExpiry}</p>
                                )}
                            </div>
                        </div>
                        <div className="">
                            <div className="c__input">
                                <FormLabel required={true}>
                                    {t('forms.cardCvc')}
                                </FormLabel>
                                <div className="relative">
                                    <CardCvcElement options={{style}}/>
                                    <Icon name="cvc" className="absolute right-2 top-2 block h-auto w-6"/>
                                </div>
                                {errors.cardCvc && (
                                    <p className="error">{errors.cardCvc}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div>
                        {errors.global && (
                            <p className="error">{errors.global}</p>
                        )}
                    </div>
                    <div className="mt-6 flex flex-row items-center justify-end gap-3">
                        <Button small style="border" onClick={() => props.onCancel()}>
                            {t('cancel')}
                        </Button>
                        <Button small type="button" onClick={handleSubmit}>
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </LoadingContainer>
        </div>
    )
}

export default CreditCardForm
